import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { Effect } from "effect";
import { useCallback, useContext, useEffect, useRef } from "react";

/**
 * Notifies the server that the current user is viewing a conversation.
 * Sends presence.viewing on mount and presence.cleared on unmount.
 */
export function usePresenceViewing(conversationId: ConversationId): void {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const auth = useContext(AuthContext);

  useEffect(() => {
    if (!auth?.userId) {
      return;
    }

    const userId = auth.userId;
    const client = effectApi.getClient();

    void effectApi.runPromise(
      client["team-notification"]
        .notifyPresence({
          payload: { event: "presence.viewing", conversationId, userId },
        })
        .pipe(Effect.orDie),
    );

    return () => {
      void effectApi.runPromise(
        client["team-notification"]
          .notifyPresence({
            payload: { event: "presence.cleared", userId, conversationId },
          })
          .pipe(Effect.orDie),
      );
    };
  }, [conversationId, effectApi, auth?.userId]);
}

/**
 * Returns a stable function that notifies the server the current user is typing.
 * Calls are debounced to avoid spamming — the backend auto-clears typing after 3 seconds.
 */
export function useNotifyTyping(conversationId: ConversationId): () => void {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const auth = useContext(AuthContext);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useCallback(() => {
    if (!auth?.userId) {
      return;
    }

    const userId = auth.userId;
    const client = effectApi.getClient();

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    } else {
      void effectApi.runPromise(
        client["team-notification"]
          .notifyPresence({
            payload: { event: "presence.typing", conversationId, userId },
          })
          .pipe(Effect.orDie),
      );
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
    }, 2000);
  }, [conversationId, effectApi, auth?.userId]);
}
